import {
  addressAsked,
  addressChoice,
  ChatIntent,
  type GigDevice,
  log,
  modelJobType,
  NeedSlot,
  needPrompt,
  needRequest,
  type Ports,
  parseNeed,
  problemByMeaning,
  readScope,
  startNeed,
  tradeByMeaning,
  understandNeed,
} from "@gigsync/core";
import { useEffect, useRef, useState } from "react";
import type { Account } from "../../adapters/account.js";
import { meaningFor } from "../../adapters/meaning.js";
import { chosenModel, ModelChoice } from "../../adapters/model.js";
import { offerAlerts } from "../../adapters/push.js";
import { stopSpeaking } from "../../adapters/speech.js";
import { needConfig } from "../../card/choices.js";
import { answerMarket, routedMarket } from "../../estimate/market.js";
import { fetchEstimate } from "../../estimate/useEstimate.js";
import { openAddressDesk } from "../../find/addressDesk.js";
import { askWorkers, needOf } from "../../find/ask.js";
import { startFindChat, useNeed } from "../../find/findChats.js";
import {
  answerNeed,
  beginNeed,
  jobTypeNeed,
  needAsked,
  needEstimateFound,
  needSay,
  relanguageNeed,
  showOwner,
  skillNeed,
} from "../../find/needStore.js";
import { findRoute } from "../../find/route.js";
import { skillAsked } from "../../find/skills.js";
import { useAddresses } from "../../find/useAddresses.js";
import { ASKED, useNeedSearch } from "../../find/useNeedSearch.js";
import { usePriceQuestions } from "../../find/usePriceQuestions.js";
import { useI18n } from "../../i18n.js";
import { Composer, Via } from "../../ui/Composer.js";
import { C } from "../../ui/cls.js";
import { Dock } from "../../ui/Dock.js";
import { errorKey } from "../../ui/errorKey.js";
import { PILOT_CITY } from "../../ui/places.js";
import { phoneDay } from "../jobGroups.js";
import { AddAddress } from "./AddAddress.js";
import { AddressDesk } from "./AddressDesk.js";
import { type FoundTurn, NeedTurns } from "./NeedTurns.js";
import { needMemory, withMemory } from "./remembered.js";
import "../talk.css";

interface Props {
  ports: Ports;
  account: Account;
  device: GigDevice;
  onAsked: () => void;
}

/** The phone has a usable model now: it decides the price questions (ADR 0041 §2); lite mode asks the fixed ones. */
const modelOn = () => chosenModel.answering() !== ModelChoice.lite;
const failure = (err: unknown) => ({ error: err instanceof Error ? err.message : String(err) });

/**
 * Find as a conversation, the way workers make their card in Talk: the owner says what they need in their own words,
 * by voice or typing, and the agent asks only what is missing, reads it back, searches, and asks the workers they
 * pick; a chat addressed to one worker skips the search and, after the yes, asks them alone. In English, the phone's own model (when there is one) helps read the first sentence; the word lists decide.
 * Each conversation is one of the household's chats (`find/findChats.ts`); once its workers were asked it stays to
 * read, with a way to ask for something else in a new chat.
 */
export function FindChat({ ports, account, device, onAsked }: Props) {
  const i18n = useI18n();
  const { lang, t } = i18n;
  const { ready, open, state, turns, to } = useNeed();
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const current = state ? needPrompt(state) : undefined;

  // A chat with nothing in it starts a new need; one under way carries on where it was. What the agent remembers
  // from past jobs comes along, with the owner's setting: offered first ("Use it?"), or filled in and said.
  const empty = ready && !state && turns.length === 0;
  const { saved, added } = useAddresses(ports, open, state?.config.addresses);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    if (!empty || !saved) return;
    const now = ports.clock.now();
    const config = { ...needConfig(lang, phoneDay(now)), hour: now.getHours(), addresses: saved.map(addressChoice) };
    void needMemory(ports, account).then((remembered) =>
      beginNeed(open, startNeed({ ...withMemory(config, remembered), modelQuestions: modelOn() }, to)),
    );
  }, [empty, open, to, lang, ports, account, saved]);

  // A language picked in About me part-way: the questions from here on, and what the mic listens for, follow it.
  useEffect(() => {
    if (state && state.config.own !== lang)
      relanguageNeed(open, {
        ...withMemory(needConfig(lang, state.config.today), state.config.remembered),
        hour: state.config.hour,
        ...(state.config.addresses ? { addresses: state.config.addresses } : {}),
      });
  }, [state, open, lang]);

  // Only a new turn scrolls the chat down: ticking a worker rewrites its turn, and must leave the list where it is.
  useEffect(() => {
    if (turns.length > 0) end.current?.scrollIntoView({ block: "end" });
  }, [turns.length]);

  // Leaving Find ends a question the owner asked to hear again.
  useEffect(
    () => () => {
      void stopSpeaking();
    },
    [],
  );

  // The job as described so far, once the trade is known: the usual range for it, shown beside the budget question.
  const request = state ? needRequest(PILOT_CITY, state.draft) : undefined;
  const requestKey = request && JSON.stringify(request);
  const session = account.session;
  usePriceQuestions(ports, open, state);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on the request's JSON, not its identity
  useEffect(() => {
    if (request) void fetchEstimate(session, request).then((found) => needEstimateFound(open, found));
  }, [requestKey, session, open]);

  useNeedSearch({ state, open, account, i18n, ports, device, onAsked, saved });

  // The first answer, the need in the owner's words: in English the phone's model helps read it, and in every
  // language its scope is read into chips the owner confirms before the estimate is asked (ADR 0033 §5).
  async function send(text: string, via: Via) {
    if (!state || busy || reading) return;
    const chat = open;
    // "Change my address", "नया पता जोड़ो": the address turn opens in the chat, whatever was being asked (ADR 0050 §6).
    if (addressAsked(text)) {
      showOwner(chat, text);
      return openAddressDesk();
    }
    const aloud = via === Via.voice;
    const first = state.slot === NeedSlot.trade;
    if (first) {
      showOwner(chat, text);
      setReading(true);
    }
    const route = await findRoute(ports, lang, text, state.slot);
    // "What do painters charge?" is answered in the chat from the estimate route (ADR 0039 §2); the need waits.
    const market = route.intent === ChatIntent.market ? routedMarket(text, route.slots) : undefined;
    if (market) {
      if (!first) showOwner(chat, text);
      setReading(true);
      const answer = await answerMarket(account.session, ports.model, lang, market);
      setReading(false);
      return needSay(chat, { key: "estimate.answer", params: { text: answer.text } });
    }
    const meaning = meaningFor(ports);
    if (!first) {
      // At the clarifying question, the kind of job the answer sounds like fills the price driver (ADR 0042).
      const asked = state.slot === NeedSlot.detail ? state.draft.trade : undefined;
      const problemKind = asked && meaning ? await problemByMeaning(meaning.problems, asked, text) : undefined;
      return answerNeed(chat, text, aloud, problemKind ? { problemKind } : {});
    }
    const { areas, today } = state.config;
    const hint = await understandNeed(ports.model, text, { areas, lang });
    // The word lists first, then a language model's reading, then the trade the sentence most sounds like (ADR 0042).
    const heard =
      parseNeed(text, { areas, today }).trade ??
      hint.trade ??
      (meaning && (await tradeByMeaning(meaning.trades, text)));
    if (heard) hint.trade = heard;
    const trade = heard ?? state.draft.trade;
    const problemKind = trade && meaning ? await problemByMeaning(meaning.problems, trade, text) : undefined;
    if (problemKind) hint.problemKind = problemKind;
    // Outside the trades: a skill workers list, matched on this phone (ADR 0047); else the phone's model may name the
    // work, and the need goes on to its questions and estimate.
    const skill = trade ? undefined : await skillAsked(ports, account.session, text, state.to?.skills);
    if (skill) {
      setReading(false);
      return skillNeed(chat, skill, text);
    }
    const jobType = !trade && state.draft.modelAsk ? await modelJobType(ports.model, text, lang) : undefined;
    if (jobType) {
      setReading(false);
      return jobTypeNeed(chat, jobType, text);
    }
    const scope = trade ? await readScope(ports.model, text, { trade, lang }) : [];
    setReading(false);
    answerNeed(chat, text, aloud, { ...hint, scope }, true);
  }

  async function ask(turn: FoundTurn) {
    // Asking workers is when this household starts to have news to hear about: the browser may ask, once.
    void offerAlerts();
    const need = state && needOf(state, i18n, ports.clock.now(), saved);
    if (busy || !need) return;
    const chat = open;
    setBusy(true);
    try {
      await askWorkers(
        ports,
        device,
        need,
        turn.workers.filter((w) => turn.picked.includes(w.user)),
      );
      needAsked(chat, ASKED);
      onAsked();
    } catch (err) {
      log.warn("asking workers failed", failure(err));
      needSay(chat, { key: errorKey(err) });
    } finally {
      setBusy(false);
    }
  }

  const conversation = (
    <NeedTurns
      chat={open}
      state={state}
      turns={turns}
      busy={busy}
      onAsk={(turn) => void ask(turn)}
      onAddAddress={() => setAdding(true)}
    />
  );
  const desk = (
    <AddressDesk
      ports={ports}
      session={account.session}
      added={(a) => added(open, a)}
      said={(line) => needSay(open, line)}
    />
  );
  const form = adding && (
    <AddAddress chat={open} session={account.session} ports={ports} added={added} onDone={() => setAdding(false)} />
  );
  // The workers were asked: the chat stays to read, and asking for something else opens a new one.
  if (ready && !state && turns.length > 0)
    return (
      <>
        {conversation}
        <div ref={end} />
        <Dock>
          <button type={C.button} className={C.wide} onClick={() => startFindChat()}>
            {t("chats.new_need")}
          </button>
        </Dock>
      </>
    );
  const waiting =
    !state || state.slot === NeedSlot.done || state.slot === NeedSlot.found || state.slot === NeedSlot.outside;
  return (
    <>
      {conversation}
      {desk}
      {form}
      {reading && (
        <p className={C.muted} role="status">
          {t("need.reading")}
        </p>
      )}
      <div ref={end} />
      <Dock>
        <Composer
          label={t("need.composer")}
          placeholder={t("need.placeholder")}
          onSend={(text, via) => void send(text, via)}
          busy={busy || reading || waiting}
          hints={current?.hints ?? []}
          numeric={current?.numeric ?? false}
          onDeviceOnly={current?.secret ?? false}
        />
      </Dock>
    </>
  );
}
